import { SetStateAction, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Dropdown } from 'primereact/dropdown';
import { Input } from '../Input/Input';
import { IOption, ISelect, SelectChangeVariant } from './Select.types';
import styles from './Select.module.scss';

export const Select: React.FC<ISelect> = (props: ISelect) => {
  const {
    selectedOption,
    onChange,
    options,
    label,
    required,
    disabled = false,
    showClear = false,
    searchable = false,
    hasChanged = false,
    highlightOnChange = true,
    placeholder = 'Select...',
    filterPlaceholder = '',
    highlightBorder = true,
    selectChangedStyle = SelectChangeVariant.Default,
    defaultSearchedText,
    onSearchTextChange,
    icon,
    className = '',
    dropdownWrapperClassName = '',
    appendTo = 'self',
    ...restProps
  } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [searchedText, setSearchedText] = useState(defaultSearchedText || '');
  const hasError = !disabled && touched && required && !selectedOption;
  const dropdownClassName = classNames(className, styles['ds-select-dropdown-wrapper'], {
    [styles['disabled-select-dropdown']]: disabled,
    [styles['no-focus-style']]: !highlightBorder,
    [styles['dropdown-error']]: hasError,
  });

  useEffect(() => {
    setSearchedText(defaultSearchedText || '');
  }, [defaultSearchedText]);

  const onShow = () => {
    setIsDropdownOpen(true);
    setTimeout(() => {
      inputRef.current && inputRef.current.focus();
    });
  };

  const onHide = () => {
    setIsDropdownOpen(false);
  };

  const onFilterOptions = (event: { target: { value: SetStateAction<string> } }) => {
    const value = event.target.value as string;
    setSearchedText(value);
    if (onSearchTextChange) onSearchTextChange(value);

    setTimeout(() => {
      if (dropdownRef.current) {
        const optionsElement = dropdownRef.current.querySelector('.p-dropdown-panel') as HTMLElement;
        const { top } = window.getComputedStyle(optionsElement) || { top: '' };
        if (top && parseInt(top) < 0) {
          optionsElement.style.top = `-${optionsElement.clientHeight}px`;
        }
      }
    });
  };

  const onValueChange = (value: string | number) => {
    setTouched(true);
    const option = options.find((item) => item.value === value) ?? null;
    onChange(option);
    setSearchedText('');
    checkAndSetTitleToSelectedOption(option);
  };

  const checkAndSetTitleToSelectedOption = (option: IOption | null) => {
    setTimeout(() => {
      if (dropdownRef.current) {
        const element = dropdownRef.current.querySelector('.p-dropdown .p-dropdown-label.p-inputtext');
        if (element) {
          if (element.clientWidth < element.scrollWidth && option?.label) {
            element.setAttribute('title', option.label);
          } else {
            element?.removeAttribute('title');
          }
        }
      }
    }, 100);
  };

  const customExpandIcon = () => {
    return <img src={icon} alt="Expand Icon" style={{ width: '16px', height: '16px' }} />;
  };

  const customCollapseIcon = () => {
    return (
      <img
        src={icon}
        alt="Collapse Icon"
        style={{ width: '16px', height: '16px', transform: 'rotate(180deg)' }} // Rotate the icon
      />
    );
  };

  // Filter options based on searchedText if searchable is true
  let filteredOptions = options;
  // if `defaultSearchedText` text is passed then we are filtering on the backend and we don't need to filter here
  // there are cases where the filter is done on more fields in the backend than just the label so we want to avoid filtering here
  if (searchable && !defaultSearchedText)
    filteredOptions = options.filter(({ label }) => label?.toLowerCase().includes(searchedText.toLowerCase()));

  return (
    <div className={dropdownClassName} ref={dropdownRef} data-testid="select-dropdown">
      {label && (
        <label>
          {label} {required && <span className="required-asterisk">*</span>}
        </label>
      )}
      <div
        className={classNames(styles['p-dropdown-element-wrapper'], dropdownWrapperClassName, {
          [styles['input-changed']]: highlightOnChange && hasChanged,
          [styles[selectChangedStyle]]: highlightOnChange && hasChanged,
        })}
      >
        {searchable && isDropdownOpen && (
          <Input
            value={searchedText}
            ref={inputRef}
            className={styles['dropdown-options-searchable-input']}
            placeholder={filterPlaceholder}
            onChange={onFilterOptions}
          />
        )}
        <Dropdown
          {...restProps}
          showClear={showClear}
          disabled={disabled}
          value={selectedOption?.value}
          options={filteredOptions}
          placeholder={searchable ? filterPlaceholder : placeholder}
          appendTo={appendTo}
          onChange={({ value }) => onValueChange(value)}
          onShow={onShow}
          onHide={onHide}
          dropdownIcon={icon ? customExpandIcon : null}
          collapseIcon={icon ? customCollapseIcon : null}
        />
      </div>
      {hasError && <span className="error-message">Field is required</span>}
    </div>
  );
};
